import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Loader2,
  CloudSun,
  Sun,
  Cloud,
  X,
  FileSpreadsheet,
  ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid';

import { DailyEnvironmentalDataService } from '../services/dailyEnvironmentalData.service';
import { notifyEnvironmentalDataUpdated } from '../../../shared/utils/environmentalDataEvents';
import type {
  DailyEnvironmentalData,
  WeatherCondition,
} from '../../../types/api';

const WEATHER_OPTIONS: {
  value: WeatherCondition;
  label: string;
  icon: React.ElementType;
}[] = [
  { value: 'clear', label: 'Clear', icon: Sun },
  { value: 'cloudy', label: 'Cloudy', icon: Cloud },
  { value: 'sunny', label: 'Sunny', icon: CloudSun },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const todayIsoDate = (): string => new Date().toISOString().slice(0, 10);

const ManageDailyEnvironmentalData: React.FC = () => {
  const navigate = useNavigate();

  const [entries, setEntries] = useState<DailyEnvironmentalData[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [todaysEntry, setTodaysEntry] =
    useState<DailyEnvironmentalData | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // MUI DataGrid uses zero-based pages.
  // The API continues to use one-based pages.
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Applied filters — these actually drive the API request.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Draft filters — these are only applied when Apply is clicked.
  const [draftFromDate, setDraftFromDate] = useState('');
  const [draftToDate, setDraftToDate] = useState('');

  const [formData, setFormData] = useState({
    ambient_temperature: '',
    humidity: '',
    atm_pressure_mmhg: '',
    weather_condition: 'clear' as WeatherCondition,
  });

  const today = todayIsoDate();

  /**
   * Load server-side paginated history.
   *
   * MUI DataGrid page is zero-based, while the API expects
   * a one-based page number.
   */
  const loadEntries = useCallback(
    async (
      page: number,
      limit: number,
      from: string,
      to: string
    ) => {
      setIsLoading(true);

      try {
        const response = await DailyEnvironmentalDataService.list(
          page + 1,
          limit,
          {
            entry_date_from: from || undefined,
            entry_date_to: to || undefined,
          }
        );

        setEntries(response.data);
        setTotalItems(response.total);
      } catch (error: any) {
        toast.error(
          error.response?.data?.detail ||
            error.message ||
            'Failed to load daily environmental data'
        );

        setEntries([]);
        setTotalItems(0);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadEntries(currentPage, itemsPerPage, fromDate, toDate);
  }, [
    currentPage,
    itemsPerPage,
    fromDate,
    toDate,
    loadEntries,
  ]);

  /**
   * Load today's entry independently of the paginated table.
   */
  const loadTodaysEntry = useCallback(async () => {
    try {
      const entry = await DailyEnvironmentalDataService.getToday();

      setTodaysEntry(entry);

      setFormData({
        ambient_temperature: String(entry.ambient_temperature),
        humidity: String(entry.humidity),
        atm_pressure_mmhg: String(entry.atm_pressure_mmhg),
        weather_condition: entry.weather_condition,
      });
    } catch (error: any) {
      // 404 means no entry exists for today.
      setTodaysEntry(null);

      setFormData({
        ambient_temperature: '',
        humidity: '',
        atm_pressure_mmhg: '',
        weather_condition: 'clear',
      });
    }
  }, []);

  useEffect(() => {
    loadTodaysEntry();
  }, [loadTodaysEntry]);

  /**
   * MUI DataGrid pagination handler.
   *
   * page is zero-based.
   * pageSize is the selected number of rows per page.
   */
  const handlePaginationModelChange = (
    model: GridPaginationModel
  ) => {
    if (model.page !== currentPage) {
      setCurrentPage(model.page);
    }

    if (model.pageSize !== itemsPerPage) {
      setItemsPerPage(model.pageSize);

      // When page size changes, start from the first page.
      setCurrentPage(0);
    }
  };

  const handleApplyDateFilters = () => {
    setFromDate(draftFromDate);
    setToDate(draftToDate);

    // Always return to the first page when changing filters.
    setCurrentPage(0);
  };

  /**
   * Export uses the currently applied date range,
   * not the draft date range.
   */
  const handleExport = async () => {
    setIsExporting(true);

    try {
      const filters = {
        entry_date_from: fromDate || undefined,
        entry_date_to: toDate || undefined,
      };

      const blob =
        await DailyEnvironmentalDataService.getExportBlob(filters);

      const filename =
        DailyEnvironmentalDataService.buildExportFilename(filters);

      DailyEnvironmentalDataService.triggerExportDownload(
        blob,
        filename
      );
    } catch (error: any) {
      toast.error(
        error.response?.data?.detail ||
          error.message ||
          'Failed to export daily environmental data'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const openAddForm = () => {
    setFormData({
      ambient_temperature: todaysEntry
        ? String(todaysEntry.ambient_temperature)
        : '',
      humidity: todaysEntry
        ? String(todaysEntry.humidity)
        : '',
      atm_pressure_mmhg: todaysEntry
        ? String(todaysEntry.atm_pressure_mmhg)
        : '',
      weather_condition:
        todaysEntry?.weather_condition || 'clear',
    });

    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const ambientTemperature = parseFloat(
      formData.ambient_temperature
    );

    const humidity = parseFloat(formData.humidity);

    const atmPressureMmhg = parseFloat(
      formData.atm_pressure_mmhg
    );

    if (isNaN(ambientTemperature)) {
      toast.error('Please enter a valid ambient temperature');
      return;
    }

    if (isNaN(humidity) || humidity < 0 || humidity > 100) {
      toast.error('Please enter a valid humidity (0–100 %)');
      return;
    }

    if (isNaN(atmPressureMmhg) || atmPressureMmhg <= 0) {
      toast.error(
        'Please enter a valid atmospheric pressure (mm of Hg)'
      );
      return;
    }

    setIsSaving(true);

    try {
      await DailyEnvironmentalDataService.upsert({
        ambient_temperature: ambientTemperature,
        humidity,
        atm_pressure_mmhg: atmPressureMmhg,
        weather_condition: formData.weather_condition,
      });

      toast.success(
        'Daily environmental data saved successfully'
      );

      setIsFormOpen(false);

      await Promise.all([
        loadEntries(
          currentPage,
          itemsPerPage,
          fromDate,
          toDate
        ),
        loadTodaysEntry(),
      ]);

      notifyEnvironmentalDataUpdated();
    } catch (error: any) {
      toast.error(
        error.response?.data?.detail ||
          error.message ||
          'Failed to save daily environmental data'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * MUI DataGrid columns.
   *
   * `flex` makes the columns responsive.
   * `minWidth` prevents important values from becoming too narrow.
   */
  const columns: GridColDef<DailyEnvironmentalData>[] = [
    {
      field: 'entry_date',
      headerName: 'Date',
      flex: 1,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <span className="text-sm font-medium text-gray-900">
          {params.row.entry_date}
        </span>
      ),
    },
    {
      field: 'ambient_temperature',
      headerName: 'Ambient Temp',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <span className="text-sm text-gray-900">
          {params.row.ambient_temperature}°C
        </span>
      ),
    },
    {
      field: 'humidity',
      headerName: 'Humidity',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <span className="text-sm text-gray-900">
          {params.row.humidity != null
            ? `${params.row.humidity}%`
            : '—'}
        </span>
      ),
    },
    {
      field: 'atm_pressure_mmhg',
      headerName: 'Atm. Pressure',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <span className="text-sm text-gray-900">
          {params.row.atm_pressure_mmhg} mmHg
        </span>
      ),
    },
    {
      field: 'weather_condition',
      headerName: 'Weather',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const option = WEATHER_OPTIONS.find(
          (o) => o.value === params.row.weather_condition
        );

        const Icon = option?.icon || CloudSun;

        return (
          <div className="flex items-center gap-2 h-full">
            <span className="text-sm text-gray-900 capitalize flex items-center gap-1.5">
            <Icon className="h-4 w-4 text-gray-400" />
            {params.row.weather_condition}
          </span>
          </div>
        );
      },
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 1,
      minWidth: 150,
      sortable: false,
      valueGetter: ( params:any): any =>
        params.row.updated_by || params.row.created_by || '—',
      renderCell: (params) => (
        <span className="text-sm text-gray-600">
          {params.value}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500 border-b border-gray-200 pb-4 mb-6">
        <button
          onClick={() => navigate('/')}
          className="hover:text-gray-700 transition-colors cursor-pointer"
          title="Back to Dashboard"
        >
          Dashboard
        </button>

        <ChevronRight className="h-4 w-4" />

        <span className="text-primary-600 font-medium">
          Daily Environmental Data
        </span>
      </div>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <CloudSun className="h-8 w-8 flex-shrink-0 text-primary-600 mt-0.5" />

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Daily Environmental Data
            </h1>

            <p className="mt-1 text-sm text-gray-600">
              Set today's temperature, humidity, pressure, and
              weather condition.
            </p>
          </div>
        </div>

        <button
          onClick={openAddForm}
          className="btn-primary flex items-center gap-2 whitespace-nowrap"
        >
          <Plus className="h-4 w-4" />

          {todaysEntry
            ? "Update Today's Data"
            : 'Add Data'}
        </button>
      </div>

      {/* Add / Update Form */}
      {isFormOpen && (
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              {todaysEntry
                ? "Update Today's Environmental Data"
                : "Add Today's Environmental Data"}
            </h2>

            <button
              onClick={() => setIsFormOpen(false)}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>

                <input
                  type="text"
                  value={today}
                  readOnly
                  disabled
                  className="input w-full bg-gray-100 cursor-not-allowed"
                  title="Only today's data can be added or updated"
                />
              </div>

              {/* Temperature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ambient Temperature
                  <span className="text-gray-500 ml-1">
                    (°C)
                  </span>
                  <span className="text-red-500 ml-1">
                    *
                  </span>
                </label>

                <input
                  type="number"
                  step="0.1"
                  value={formData.ambient_temperature}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ambient_temperature:
                        e.target.value,
                    }))
                  }
                  className="input w-full"
                  placeholder="e.g., 26"
                  required
                />
              </div>

              {/* Humidity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Humidity
                  <span className="text-gray-500 ml-1">
                    (%)
                  </span>
                  <span className="text-red-500 ml-1">
                    *
                  </span>
                </label>

                <input
                  type="number"
                  step="0.1"
                  min={0}
                  max={100}
                  value={formData.humidity}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      humidity: e.target.value,
                    }))
                  }
                  className="input w-full"
                  placeholder="e.g., 55"
                  required
                />
              </div>

              {/* Atmospheric Pressure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Atmospheric Pressure
                  <span className="text-gray-500 ml-1">
                    (mm of Hg)
                  </span>
                  <span className="text-red-500 ml-1">
                    *
                  </span>
                </label>

                <input
                  type="number"
                  step="0.1"
                  value={formData.atm_pressure_mmhg}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      atm_pressure_mmhg:
                        e.target.value,
                    }))
                  }
                  className="input w-full"
                  placeholder="e.g., 760"
                  required
                />
              </div>
            </div>

            {/* Weather */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Weather Condition
                <span className="text-red-500 ml-1">
                  *
                </span>
              </label>

              <div className="grid grid-cols-3 gap-3 max-w-md">
                {WEATHER_OPTIONS.map((option) => {
                  const Icon = option.icon;

                  const isSelected =
                    formData.weather_condition ===
                    option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          weather_condition:
                            option.value,
                        }))
                      }
                      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 py-4 px-3 transition-colors ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Icon
                        className={`h-7 w-7 ${
                          isSelected
                            ? 'text-primary-600'
                            : 'text-gray-400'
                        }`}
                      />

                      <span className="text-sm font-medium">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSaving}
                className="btn-primary disabled:opacity-60"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-4 ml-auto">
          {/* From Date */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              From:
            </label>

            <input
              type="date"
              value={draftFromDate}
              max={draftToDate || undefined}
              onChange={(e) =>
                setDraftFromDate(e.target.value)
              }
              className="input text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* To Date */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              To:
            </label>

            <input
              type="date"
              value={draftToDate}
              min={draftFromDate || undefined}
              onChange={(e) =>
                setDraftToDate(e.target.value)
              }
              className="input text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Apply */}
          <button
            type="button"
            onClick={handleApplyDateFilters}
            className="btn-primary text-sm px-3 py-1.5"
          >
            Apply
          </button>

          {/* Export */}
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            title="Export to Excel"
            aria-label="Export to Excel"
            className="p-2 rounded-md text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-all duration-200 border border-emerald-200 disabled:opacity-60"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Data Grid */}
      <div className="card overflow-hidden">
        <div
          className="w-full"
          style={{
            minHeight: 400,
          }}
        >
          <DataGrid
            rows={entries}
            columns={columns}
            getRowId={(row) =>
              row.id ?? `${row.entry_date}`
            }
            rowCount={totalItems}
            loading={isLoading}
            pagination
            paginationMode="server"
            paginationModel={{
              page: currentPage,
              pageSize: itemsPerPage,
            }}
            onPaginationModelChange={
              handlePaginationModelChange
            }
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            disableRowSelectionOnClick
            autoHeight
            localeText={{
              noRowsLabel: fromDate || toDate
                ? 'No entries found for the selected date range.'
                : 'No daily environmental data entries yet.',
            }}
            sx={{
              border: 'none',

              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
              },

              '& .MuiDataGrid-columnHeader': {
                fontWeight: 600,
                color: '#374151',
              },


              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f9fafb',
              },

              '& .MuiDataGrid-virtualScroller': {
                minHeight:
                  entries.length === 0 ? 180 : undefined,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ManageDailyEnvironmentalData;
